/* Load synchronously in the contact page head, before analytics. No contact data or token is persisted. */
(() => {
  'use strict';

  const page = new URL(window.location.href);
  if (!/^\/pages\/contact\/?$/.test(page.pathname) || page.searchParams.get('intent') !== 'offer') return;

  const fragment = new URLSearchParams(page.hash.slice(1));
  const candidate = fragment.get('proda-offer') || '';
  const token = /^[A-Za-z0-9._~-]{20,2048}$/.test(candidate) ? candidate : '';
  // Remove the bearer from the address bar before any page analytics can read it.
  // Reloading requires reopening the original DM link; deliberately do not use browser storage.
  page.hash = '';
  window.history.replaceState(window.history.state, '', page.pathname + page.search);

  const ENDPOINT = 'https://proda-dm-service.vercel.app/api/offer';
  const STEPS = new Set(['email', 'sms', 'complete', 'pending', 'unavailable']);
  const script = document.currentScript;
  const artSource = script?.getAttribute('data-offer-image') || '';
  let busy = false;
  let section;
  let stage;
  let headline;
  let progress;
  let errorBox;
  let signupIdentity;

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function link(text, href, className = '') {
    const node = element('a', className, text);
    node.href = href;
    return node;
  }

  function button(text, listener, className = 'proda-social-offer__button') {
    const node = element('button', className, text);
    node.type = 'button';
    node.addEventListener('click', listener);
    return node;
  }

  function policies(container, withTerms = false) {
    if (withTerms) {
      container.append(link('Terms', '/policies/terms-of-service'), document.createTextNode(' · '));
    }
    container.append(link('Privacy', '/policies/privacy-policy'));
  }

  function validatedOfferUrl(value) {
    if (typeof value !== 'string') return '';
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || !['drinkproda.com', 'www.drinkproda.com'].includes(url.hostname)) return '';
      if (url.port || url.username || url.password || url.hash) return '';
      return url.href;
    } catch {
      return '';
    }
  }

  function setBusy(value) {
    busy = value;
    section?.setAttribute('aria-busy', String(value));
    stage?.querySelectorAll('button, input').forEach((control) => { control.disabled = value; });
  }

  function showError(message) {
    errorBox.textContent = message;
    errorBox.hidden = !message;
  }

  function boundedMessage(value, limit = 400) {
    return typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, limit) : '';
  }

  function focusStage() {
    headline.focus({ preventScroll: true });
  }

  async function request(action, fields = {}) {
    if (busy) return;
    setBusy(true);
    showError('');
    signupIdentity ||= window.PRODA_TRACKING?.createSignupIdentity?.();
    const stagedIdentity = signupIdentity?.stage({ action, ...fields });
    const controller = new AbortController();
    let timeout;
    try {
      const deadline = new Promise((_, reject) => { timeout = window.setTimeout(() => { controller.abort(); reject(new Error('OFFER_CHECK_TIMEOUT')); }, 20000); });
      const operation = (async () => {
        const response = await window.fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal,
          body: JSON.stringify({ token, action, ...fields }),
        });
        if (!response.ok) throw new Error('OFFER_UNAVAILABLE');
        return response.json();
      })();
      const result = await Promise.race([operation, deadline]);
      if (!result || !STEPS.has(result.step)) throw new Error('INVALID_OFFER_STATE');
      if (result.step === 'complete' && !validatedOfferUrl(result.offerUrl)) throw new Error('INVALID_OFFER_LINK');
      // Step names alone do not establish consent. Only the server's current confirmed channel can identify.
      signupIdentity?.confirm(result, stagedIdentity);
      render(result);
      if (['email', 'sms'].includes(result.step)) showError(boundedMessage(result.error, 240));
    } catch {
      // A failed connection is not proof that a consent write failed. Do not resubmit it.
      render({ step: 'check' });
      showError(action === 'status'
        ? 'We could not check your signup just now. Please try checking again.'
        : 'We could not confirm the result. Check your signup before submitting anything again.');
    } finally {
      window.clearTimeout(timeout);
      setBusy(false);
    }
  }

  function formFor(channel) {
    const isEmail = channel === 'email';
    const form = element('form', 'proda-social-offer__form ph-no-capture ph-no-autocapture');
    // No generic Shopify/Klaviyo capture attribute: the signed service owns this submission.
    form.setAttribute('data-private', 'true');
    form.noValidate = true;
    const fieldId = `proda-social-offer-${channel}`;
    const label = element('label', 'proda-social-offer__label', isEmail ? 'Email address' : 'U.S. mobile number');
    label.htmlFor = fieldId;
    const input = element('input', 'proda-social-offer__input ph-no-capture ph-no-autocapture');
    input.id = fieldId;
    input.name = isEmail ? 'email' : 'phone';
    input.type = isEmail ? 'email' : 'tel';
    input.autocomplete = isEmail ? 'email' : 'tel-national';
    input.inputMode = isEmail ? 'email' : 'tel';
    input.placeholder = isEmail ? 'you@example.com' : '(555) 555-0123';
    input.required = true;
    input.maxLength = isEmail ? 254 : 24;
    input.setAttribute('data-private', 'true');
    input.setAttribute('data-1p-ignore', '');
    const consent = element('label', 'proda-social-offer__consent');
    const checkbox = element('input', 'proda-social-offer__checkbox');
    checkbox.type = 'checkbox';
    checkbox.name = 'consent';
    checkbox.required = true;
    checkbox.checked = false;
    const consentText = element('span', '', isEmail
      ? 'I agree to receive PRODA marketing emails. I can unsubscribe anytime.'
      : 'I agree to receive recurring automated PRODA marketing texts at this number.');
    consent.append(checkbox, consentText);
    const details = element('p', 'proda-social-offer__legal');
    details.id = `proda-social-offer-${channel}-details`;
    if (!isEmail) {
      details.append(document.createTextNode("Consent isn't required to buy. Msg & data rates may apply; frequency varies. Reply STOP to our texts to opt out or HELP for help. "));
    }
    policies(details, !isEmail);
    checkbox.setAttribute('aria-describedby', details.id);
    const submit = element('button', 'proda-social-offer__button', isEmail ? 'Join emails & continue' : 'Join texts & finish');
    submit.type = 'submit';
    input.addEventListener('input', () => input.setCustomValidity(''));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      input.setCustomValidity('');
      const value = input.value.trim();
      let normalized = value;
      if (!isEmail) {
        const digits = value.replace(/[^0-9]/g, '');
        normalized = digits.length === 10 ? `+1${digits}` : digits.length === 11 && digits.startsWith('1') ? `+${digits}` : '';
        if (!normalized || /[a-z]/i.test(value)) input.setCustomValidity('Enter a U.S. mobile number with its area code.');
      } else if (/[\u0000-\u001f\u007f]/.test(value)) {
        input.setCustomValidity('Enter a valid email address.');
      }
      if (!form.reportValidity() || !checkbox.checked) return;
      void request(channel, { [isEmail ? 'email' : 'phone']: normalized, consent: true });
    });
    form.append(label, input, consent, details, submit);
    return form;
  }

  function render(result) {
    stage.replaceChildren();
    section.dataset.step = result.step;
    progress.hidden = !['email', 'sms'].includes(result.step);
    const chips = progress.querySelectorAll('li');
    chips.forEach((chip, index) => {
      const active = result.step === 'email' ? index === 0 : index === 1;
      chip.classList.toggle('is-current', active);
      chip.classList.toggle('is-finished', result.step === 'sms' && index === 0);
      if (active) chip.setAttribute('aria-current', 'step');
      else chip.removeAttribute('aria-current');
    });
    if (result.step === 'email') {
      headline.textContent = 'A little more fizz.\n15% off your next case.';
      stage.append(element('p', 'proda-social-offer__intro', 'Join our emails, then finish with texts. We’ll only ask for the signup steps you still need.'), formFor('email'));
    } else if (result.step === 'sms') {
      headline.textContent = 'One last step.\nLet’s make it official.';
      stage.append(element('p', 'proda-social-offer__intro', 'Finish with PRODA texts for offers and fresh news, straight to your phone.'), formFor('sms'));
    } else if (result.step === 'complete') {
      headline.textContent = 'You’re all set.\nLet’s find your flavor.';
      stage.append(element('p', 'proda-social-offer__intro', 'Your signup is complete. No need to enter your details again. Enjoy 15% off your next eligible one-time order 🧡'));
      const shop = link('Shop with 15% off', validatedOfferUrl(result.offerUrl), 'proda-social-offer__button');
      shop.rel = 'noreferrer';
      stage.append(shop);
    } else if (result.step === 'pending') {
      headline.textContent = 'Nearly there.\nWe’re finishing up.';
      stage.append(element('p', 'proda-social-offer__intro', 'Your signup is processing. Check below in a moment. You don’t need to enter your details again.'), button('Check my signup', () => void request('status')));
    } else if (result.step === 'check') {
      headline.textContent = 'Let’s check\nwhere you left off.';
      stage.append(element('p', 'proda-social-offer__intro', 'We’ll check the last step before asking for anything else.'), button('Check my signup', () => void request('status')));
    } else {
      const message = boundedMessage(result.message);
      if (message && !/\bexpired\b/i.test(message)) {
        headline.textContent = 'Let’s find\nthe right next step.';
        stage.append(element('p', 'proda-social-offer__intro', message), link('Get help from PRODA', '/pages/contact?intent=general#proda-request', 'proda-social-offer__button'));
      } else {
        headline.textContent = 'Let’s get you\na fresh link.';
        stage.append(element('p', 'proda-social-offer__intro', 'Open the offer link in your PRODA Instagram conversation again. If it has expired, send PRODA for a fresh one.'), link('Open PRODA on Instagram', 'https://www.instagram.com/drinkproda/', 'proda-social-offer__button'));
      }
    }
    focusStage();
  }

  function mount() {
    if (document.getElementById('proda-social-offer')) return;
    section = element('section', 'proda-social-offer ph-no-capture ph-no-autocapture');
    section.id = 'proda-social-offer';
    section.setAttribute('aria-labelledby', 'proda-social-offer-title');
    section.setAttribute('data-private', 'true');
    const visual = element('div', 'proda-social-offer__visual');
    visual.append(element('p', 'proda-social-offer__wordmark', 'PRODA'));
    const art = element('div', 'proda-social-offer__art');
    try {
      const url = new URL(artSource, page.origin);
      if (artSource && url.protocol === 'https:' && [page.hostname, 'cdn.shopify.com'].includes(url.hostname)) {
        const image = element('img', 'proda-social-offer__image');
        image.src = url.href;
        image.alt = 'A colorful lineup of PRODA soda cans.';
        image.width = 720;
        image.height = 720;
        image.decoding = 'async';
        art.append(image);
      }
    } catch { /* Photography is optional; the form still works. */ }
    if (!art.childElementCount) {
      art.append(element('p', 'proda-social-offer__big-type', 'GOOD\nTASTE.\nGREAT\nCOMPANY.'));
    }
    visual.append(art, element('p', 'proda-social-offer__shipping', '2+ cases. Free standard shipping.'));
    const content = element('div', 'proda-social-offer__content');
    progress = element('ol', 'proda-social-offer__progress');
    progress.setAttribute('aria-label', 'Signup steps');
    progress.hidden = true;
    progress.append(element('li', '', '01  Email'), element('li', '', '02  Texts'));
    headline = element('h1', 'proda-social-offer__title', 'Getting your\noffer ready.');
    headline.id = 'proda-social-offer-title';
    headline.tabIndex = -1;
    stage = element('div', 'proda-social-offer__stage');
    stage.append(element('p', 'proda-social-offer__intro', 'Checking which signup steps you still need…'));
    errorBox = element('p', 'proda-social-offer__error');
    errorBox.hidden = true;
    errorBox.setAttribute('role', 'alert');
    const finePrint = element('p', 'proda-social-offer__terms', '15% off eligible one-time website orders. Free standard shipping on 2+ cases in the contiguous U.S. + DC. Excludes subscriptions and wholesale. Cannot be combined with other discounts.');
    const help = link('Need help with something else?', '/pages/contact?intent=general#proda-request', 'proda-social-offer__help');
    content.append(progress, headline, stage, errorBox, finePrint, help);
    section.append(visual, content);
    (document.querySelector('main') || document.body).prepend(section);
    if (!token) render({ step: 'unavailable' });
    else void request('status');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
